import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { Package, Clock, User, LogOut, Calendar, AlertCircle, Eye, Truck, Phone, MapPin, FileText } from 'lucide-react';
import { generateDriverRouteSheetPDF } from '../../components/ui/DriverRouteSheetPDF';
import { format, startOfWeek, endOfWeek, startOfMonth, endOfMonth, addDays, addWeeks, addMonths, subDays, subWeeks, subMonths } from 'date-fns';
import { fr } from 'date-fns/locale';
import { trackDeliveryTask, trackButtonClick, trackNavigation, trackAuth } from '../../services/analyticsService';
import BarrelDepositDetailModal from './components/BarrelDepositDetailModal';

const DriverDashboard = () => {
  const navigate = useNavigate();
  const [employee, setEmployee] = useState(null);
  const [authUserId, setAuthUserId] = useState(null);
  const [pickupTasks, setPickupTasks] = useState([]);
  const [barrelDeposits, setBarrelDeposits] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [viewType, setViewType] = useState('daily'); // 'daily', 'weekly', 'monthly'
  const [currentPeriod, setCurrentPeriod] = useState(new Date());
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [showDepositModal, setShowDepositModal] = useState(false);

  useEffect(() => {
    checkAuthAndLoadData();
  }, []);

  useEffect(() => {
    filterTasksByView();
  }, [pickupTasks, barrelDeposits, viewType, currentPeriod, selectedDate]);

  // Envoi périodique de la position GPS du chauffeur
  useEffect(() => {
    if (!authUserId) return;

    const sendLocation = () => {
      if (!navigator.geolocation) return;
      navigator.geolocation?.getCurrentPosition(
        async (position) => {
          const { latitude, longitude } = position?.coords;
          const { error: gpsError } = await supabase
            ?.from('driver_locations')
            ?.upsert({
              driver_id: authUserId,
              latitude,
              longitude,
              updated_at: new Date()?.toISOString()
            });
          if (gpsError) console.error('❌ Erreur envoi GPS:', gpsError);
        },
        (err) => console.error('❌ Erreur géolocalisation:', err),
        { enableHighAccuracy: true }
      );
    };

    sendLocation(); // envoi immédiat
    const interval = setInterval(sendLocation, 30000); // puis toutes les 30s

    return () => clearInterval(interval);
  }, [authUserId]);

  const checkAuthAndLoadData = async () => {
    try {
      console.log('🔍 Checking driver authentication...');
      
      const { data: { user }, error: userError } = await supabase?.auth?.getUser();

      if (userError || !user) {
        console.log('❌ No authenticated user - redirecting to login');
        navigate('/driver-login', { replace: true });
        return;
      }

      console.log('✅ User authenticated:', user?.email);
      setAuthUserId(user?.id);

      const { data: employeeData, error: employeeError } = await supabase
        ?.from('employees')
        ?.select('*')
        ?.eq('auth_user_id', user?.id)
        ?.single();

      if (employeeError || !employeeData) {
        console.error('❌ No employee record found');
        await supabase?.auth?.signOut();
        navigate('/driver-login', { replace: true });
        return;
      }

      console.log('✅ Employee loaded:', employeeData?.name);
      setEmployee(employeeData);

      await Promise.all([
        loadPickupTasks(employeeData?.id),
        loadBarrelDeposits(employeeData?.id)
      ]);
      
    } catch (err) {
      console.error('❌ Error in checkAuthAndLoadData:', err);
      navigate('/driver-login', { replace: true });
    } finally {
      setLoading(false);
    }
  };

  const loadPickupTasks = async (employeeId) => {
    try {
      const { data: tasks, error: tasksError } = await supabase
        ?.from('pickup_tasks')
        ?.select(`
          *,
          shipments!pickup_tasks_shipment_id_fkey (
            reference,
            sender_name
          )
        `)
        ?.eq('driver_id', employeeId)
        ?.order('date', { ascending: true })
        ?.order('time_window', { ascending: true });

      if (tasksError) {
        console.error('❌ Error loading pickup tasks:', tasksError);
        setPickupTasks([]);
        return;
      }

      console.log(`✅ Loaded ${tasks?.length || 0} pickup tasks`);
      setPickupTasks(tasks || []);
    } catch (err) {
      console.error('❌ Error loading pickup tasks:', err);
      setPickupTasks([]);
    }
  };

  const loadBarrelDeposits = async (employeeId) => {
    try {
      const { data: deposits, error: depositsError } = await supabase
        ?.from('barrel_deposits')
        ?.select('*')
        ?.eq('assigned_driver_id', employeeId)
        ?.order('planned_collection_date', { ascending: true })
        ?.order('time_slot', { ascending: true });

      if (depositsError) {
        throw depositsError;
      }

      console.log(`✅ Loaded ${deposits?.length || 0} barrel deposits`);
      setBarrelDeposits(deposits || []);
    } catch (err) {
      console.error('❌ Error loading barrel deposits:', err);
      setError('Erreur lors du chargement des tâches de dépôt');
    }
  };

  const filterTasksByView = () => {
    if (!pickupTasks?.length && !barrelDeposits?.length) {
      setFilteredTasks([]);
      return;
    }

    const period = new Date(viewType === 'daily' ? selectedDate : currentPeriod);
    let weekStart, weekEnd, monthStart, monthEnd;

    if (viewType === 'weekly') {
      weekStart = startOfWeek(period, { locale: fr, weekStartsOn: 1 });
      weekEnd = endOfWeek(period, { locale: fr, weekStartsOn: 1 });
    } else if (viewType === 'monthly') {
      monthStart = startOfMonth(period);
      monthEnd = endOfMonth(period);
    }

    let filteredPickups = [];
    if (viewType === 'daily') {
      filteredPickups = pickupTasks?.filter(task => {
        const taskDate = new Date(task?.date)?.toISOString()?.split('T')?.[0];
        return taskDate === selectedDate;
      });
    } else if (viewType === 'weekly') {
      filteredPickups = pickupTasks?.filter(task => {
        const taskDate = new Date(task?.date);
        return taskDate >= weekStart && taskDate <= weekEnd;
      });
    } else if (viewType === 'monthly') {
      filteredPickups = pickupTasks?.filter(task => {
        const taskDate = new Date(task?.date);
        return taskDate >= monthStart && taskDate <= monthEnd;
      });
    }

    let filteredDeposits = [];
    if (viewType === 'daily') {
      filteredDeposits = barrelDeposits?.filter(deposit => {
        const depositDate = new Date(deposit?.planned_collection_date)?.toISOString()?.split('T')?.[0];
        return depositDate === selectedDate;
      });
    } else if (viewType === 'weekly') {
      filteredDeposits = barrelDeposits?.filter(deposit => {
        const depositDate = new Date(deposit?.planned_collection_date);
        return depositDate >= weekStart && depositDate <= weekEnd;
      });
    } else if (viewType === 'monthly') {
      filteredDeposits = barrelDeposits?.filter(deposit => {
        const depositDate = new Date(deposit?.planned_collection_date);
        return depositDate >= monthStart && depositDate <= monthEnd;
      });
    }

    if (filteredPickups?.length === 0 && filteredDeposits?.length === 0) {
      console.log('ℹ️ No tasks for selected period - showing all pending tasks');
      
      filteredPickups = pickupTasks?.filter(task => 
        task?.status !== 'completed' && task?.status !== 'cancelled'
      );
      
      filteredDeposits = barrelDeposits?.filter(deposit => 
        deposit?.collection_status !== 'completed' && deposit?.collection_status !== 'cancelled'
      );
    }

    const transformedDeposits = filteredDeposits?.map(deposit => ({
      ...deposit,
      taskType: 'depot',
      date: deposit?.planned_collection_date,
      time_window: deposit?.time_slot,
      status: deposit?.collection_status
    }));

    const transformedPickups = filteredPickups?.map(pickup => ({
      ...pickup,
      taskType: 'ramassage'
    }));

    const combined = [...transformedPickups, ...transformedDeposits]?.sort((a, b) => {
      const dateA = new Date(a?.date);
      const dateB = new Date(b?.date);
      if (dateA?.getTime() !== dateB?.getTime()) {
        return dateA - dateB;
      }
      return (a?.time_window || '')?.localeCompare(b?.time_window || '');
    });

    console.log(`✅ Filtered tasks: ${transformedPickups?.length} pickups + ${transformedDeposits?.length} deposits = ${combined?.length} total`);
    setFilteredTasks(combined);
  };

  const handlePreviousPeriod = () => {
    if (viewType === 'daily') {
      const newDate = subDays(new Date(selectedDate), 1);
      setSelectedDate(newDate?.toISOString()?.split('T')?.[0]);
    } else if (viewType === 'weekly') {
      setCurrentPeriod(subWeeks(currentPeriod, 1));
    } else if (viewType === 'monthly') {
      setCurrentPeriod(subMonths(currentPeriod, 1));
    }
  };

  const handleNextPeriod = () => {
    if (viewType === 'daily') {
      const newDate = addDays(new Date(selectedDate), 1);
      setSelectedDate(newDate?.toISOString()?.split('T')?.[0]);
    } else if (viewType === 'weekly') {
      setCurrentPeriod(addWeeks(currentPeriod, 1));
    } else if (viewType === 'monthly') {
      setCurrentPeriod(addMonths(currentPeriod, 1));
    }
  };

  const getPeriodLabel = () => {
    const period = new Date(viewType === 'daily' ? selectedDate : currentPeriod);
    
    if (viewType === 'daily') {
      return format(period, 'EEEE d MMMM yyyy', { locale: fr });
    } else if (viewType === 'weekly') {
      let weekStart = startOfWeek(period, { locale: fr, weekStartsOn: 1 });
      let weekEnd = endOfWeek(period, { locale: fr, weekStartsOn: 1 });
      return `${format(weekStart, 'd MMM', { locale: fr })} - ${format(weekEnd, 'd MMM yyyy', { locale: fr })}`;
    } else if (viewType === 'monthly') {
      return format(period, 'MMMM yyyy', { locale: fr });
    }
  };

  const handleOpenEntireRoute = () => {
    if (!filteredTasks?.length) {
      setError('Aucune tâche à ouvrir pour cette période');
      return;
    }

    const optimizedTasks = [...filteredTasks]?.sort((a, b) => {
      const zoneA = a?.client_zone || '';
      const zoneB = b?.client_zone || '';
      if (zoneA !== zoneB) {
        return zoneA?.localeCompare(zoneB);
      }
      return (a?.time_window || '')?.localeCompare(b?.time_window || '');
    });

    const origin = encodeURIComponent(`${optimizedTasks?.[0]?.client_address}, ${optimizedTasks?.[0]?.client_city}`);
    const destination = encodeURIComponent(`${optimizedTasks?.[optimizedTasks?.length - 1]?.client_address}, ${optimizedTasks?.[optimizedTasks?.length - 1]?.client_city}`);
    
    const waypoints = optimizedTasks
      ?.slice(1, -1)
      ?.map(task => encodeURIComponent(`${task?.client_address}, ${task?.client_city}`))
      ?.join('|');

    let mapsUrl = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=driving`;
    
    if (waypoints) {
      mapsUrl += `&waypoints=${waypoints}`;
    }

    window?.open(mapsUrl, '_blank');
    
    setError('');
  };

  const handleGeneratePDF = () => {
    if (!employee || !filteredTasks?.length) {
      setError('Aucune tâche à exporter');
      return;
    }

    try {
      const pdfTasks = filteredTasks?.map(task => ({
        clientName: task?.client_name,
        clientPhone: task?.client_phone,
        clientAddress: task?.client_address,
        clientCity: task?.client_city,
        clientZone: task?.client_zone,
        timeWindow: task?.time_window,
        notes: task?.notes || task?.delivery_notes,
        status: task?.status,
        date: task?.date,
        taskType: task?.taskType
      }));

      const date = viewType === 'daily' ? selectedDate : currentPeriod?.toISOString()?.split('T')?.[0];
      
      generateDriverRouteSheetPDF(
        {
          name: employee?.name,
          zone: employee?.zone
        },
        pdfTasks,
        date,
        viewType
      );

      setError('');
    } catch (err) {
      console.error('❌ Error generating PDF:', err);
      setError('Erreur lors de la génération du PDF');
    }
  };

  const handleSignOut = async () => {
    try {
      trackAuth('logout', 'driver');
      await supabase?.auth?.signOut();
      navigate('/driver-login', { replace: true });
    } catch (err) {
      console.error('Error signing out:', err);
    }
  };

  const getCollectObject = (notes) => {
    if (!notes) return null;
    const match = notes?.match(/Colis à collecter\s*:\s*(.+)/i);
    if (match) return match?.[1]?.trim();
    return notes?.length > 60 ? notes?.substring(0, 60) + '...' : notes;
  };

  const getStatusBadge = (status, taskType) => {
    const badges = {
      // Pickup task statuses
      planifie: { text: 'Planifié', className: 'bg-blue-100 text-blue-800' },
      en_route: { text: 'En route', className: 'bg-indigo-100 text-indigo-800' },
      en_cours: { text: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
      collecte: { text: 'Collecté', className: 'bg-orange-100 text-orange-800' },
      depose: { text: 'Déposé', className: 'bg-purple-100 text-purple-800' },
      termine: { text: 'Terminé', className: 'bg-green-100 text-green-800' },
      annule: { text: 'Annulé', className: 'bg-red-100 text-red-800' },
      // Barrel deposit statuses
      pending: { text: 'En attente', className: 'bg-gray-100 text-gray-800' },
      scheduled: { text: 'Planifié', className: 'bg-blue-100 text-blue-800' },
      in_progress: { text: 'En cours', className: 'bg-yellow-100 text-yellow-800' },
      collected: { text: 'Livré', className: 'bg-green-100 text-green-800' },
      cancelled: { text: 'Annulé', className: 'bg-red-100 text-red-800' }
    };
    const badge = badges?.[status] || badges?.planifie;
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${badge?.className}`}>
        {badge?.text}
      </span>
    );
  };

  const handleTaskClick = (task) => {
    if (task?.taskType === 'depot') {
      console.log('Barrel deposit clicked:', task?.id);
    } else {
      trackNavigation(`/driver-pickup-detail-with-integrated-signature/${task?.id}`, '/driver/dashboard');
      navigate(`/driver-pickup-detail-with-integrated-signature/${task?.id}`);
    }
  };

  const handleDepositClick = (task) => {
    console.log('Barrel deposit clicked:', task?.id);
    setSelectedDeposit(task);
    setShowDepositModal(true);
  };

  const handleDepositModalClose = () => {
    setShowDepositModal(false);
    setSelectedDeposit(null);
  };

  const handleDepositSuccess = async () => {
    if (employee?.id) {
      await loadBarrelDeposits(employee?.id);
    }
  };

  const handleGenerateRouteSheet = async () => {
    trackButtonClick('generate_driver_route_sheet', { 
      view_type: viewType,
      tasks_count: filteredTasks?.length 
    });
    
    try {
      await generateDriverRouteSheetPDF(filteredTasks, employee, viewType, currentPeriod);
    } catch (err) {
      console.error('Error generating route sheet:', err);
    }
  };

  const handleStatusUpdate = async (taskId, newStatus) => {
    try {
      trackDeliveryTask('status_update', { id: taskId, status: newStatus });
      
      const { error } = await supabase
        ?.from('pickup_tasks')
        ?.update({ status: newStatus })
        ?.eq('id', taskId);

      if (error) throw error;

      await loadPickupTasks(employee?.id);
    } catch (err) {
      console.error('Error updating task status:', err);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 sm:h-16 sm:w-16 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-sm sm:text-base text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="mx-auto h-12 w-12 sm:h-16 sm:w-16 text-red-500 mb-4" />
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-2">Erreur</h2>
          <p className="text-sm sm:text-base text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => navigate('/driver-login')}
            className="px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[44px] text-sm sm:text-base"
          >
            Retour à la connexion
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Header */}
      <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
        <div className="px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3">
              <div className="w-8 h-8 sm:w-10 sm:h-10 bg-green-100 rounded-full flex items-center justify-center">
                <User className="text-green-600" size={18} />
              </div>
              <div>
                <h1 className="text-base sm:text-lg font-bold text-gray-900">{employee?.name}</h1>
                <p className="text-xs sm:text-sm text-gray-600">Chauffeur</p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="p-2.5 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 active:bg-red-200 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
              aria-label="Déconnexion"
            >
              <LogOut size={18} />
            </button>
          </div>
        </div>
      </header>

      <div className="px-3 sm:px-4 py-4 sm:py-6">
        {/* View Type Selector */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-3 sm:p-4 mb-4 sm:mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <h2 className="text-base sm:text-lg font-bold text-gray-900">Mes tâches</h2>
            <div className="flex space-x-2">
              <button
                onClick={() => setViewType('daily')}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  viewType === 'daily' ?'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
              >
                Jour
              </button>
              <button
                onClick={() => setViewType('weekly')}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  viewType === 'weekly' ?'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
              >
                Semaine
              </button>
              <button
                onClick={() => setViewType('monthly')}
                className={`flex-1 sm:flex-none px-3 sm:px-4 py-2 rounded-lg text-sm font-medium transition-colors min-h-[44px] ${
                  viewType === 'monthly' ?'bg-green-600 text-white' :'bg-gray-100 text-gray-700 hover:bg-gray-200 active:bg-gray-300'
                }`}
              >
                Mois
              </button>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
            <p className="text-sm text-red-600">{error}</p>
          </div>
        )}

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-gray-900">{filteredTasks?.length || 0}</p>
              </div>
              <Package className="w-10 h-10 text-blue-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Ramassages</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredTasks?.filter(t => t?.taskType === 'ramassage')?.length || 0}
                </p>
              </div>
              <Package className="w-10 h-10 text-purple-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Dépôts</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredTasks?.filter(t => t?.taskType === 'depot')?.length || 0}
                </p>
              </div>
              <Truck className="w-10 h-10 text-orange-600" />
            </div>
          </div>

          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">En cours</p>
                <p className="text-2xl font-bold text-gray-900">
                  {filteredTasks?.filter(t => t?.status === 'en_cours' || t?.status === 'in_progress')?.length || 0}
                </p>
              </div>
              <Clock className="w-10 h-10 text-yellow-600" />
            </div>
          </div>
        </div>

        {/* Tasks List */}
        <div className="space-y-3 sm:space-y-4">
          {filteredTasks?.length === 0 ? (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8 text-center">
              <Package className="mx-auto h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mb-3" />
              <p className="text-sm sm:text-base text-gray-600">Aucune tâche pour cette période</p>
            </div>
          ) : (
            (() => {
              const activeStatuses = ['planifie', 'en_route', 'en_cours'];
              const doneStatuses = ['collecte', 'depose', 'termine'];

              const activeTasks = filteredTasks?.filter(t => t?.taskType !== 'ramassage' || activeStatuses?.includes(t?.status));
              const doneTasks = filteredTasks?.filter(t => t?.taskType === 'ramassage' && doneStatuses?.includes(t?.status));

              const renderTaskCard = (task, isDone = false) => {
                const isPickup = task?.taskType === 'ramassage';
                const isDeposit = task?.taskType === 'depot';
                const collectObject = isPickup ? getCollectObject(task?.notes) : null;

                return (
                  <div
                    key={task?.id}
                    onClick={() => {
                      if (isDeposit) {
                        handleDepositClick(task);
                      } else if (isPickup) {
                        navigate(`/driver-pickup-detail-with-integrated-signature/${task?.id}`);
                      }
                    }}
                    className={`bg-white rounded-lg shadow-sm border-l-4 p-4 hover:shadow-md transition-shadow cursor-pointer ${
                      isPickup ? 'border-purple-500' : 'border-orange-500'
                    } ${isDone ? 'opacity-60' : ''}`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-3 gap-2">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          {task?.taskType === 'depot' ? (
                            <Truck className="text-orange-600 flex-shrink-0" size={18} />
                          ) : (
                            <Package className="text-purple-600 flex-shrink-0" size={18} />
                          )}
                          <span className="text-xs font-semibold text-gray-500 uppercase">
                            {task?.taskType === 'depot' ? 'Dépôt Barrique' : 'Ramassage'}
                          </span>
                        </div>
                        <h3 className="text-base sm:text-lg font-bold text-gray-900 break-all">
                          {task?.taskType === 'depot' 
                            ? `${task?.quantity}x ${task?.barrel_type === 'noir_270l' ? 'Barrique Noire 270L' : 'Barrique Bleue 220L'}`
                            : task?.shipments?.reference
                          }
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-600 mt-1">
                          {task?.taskType === 'depot' ? task?.client_name : task?.shipments?.sender_name}
                        </p>
                      </div>
                      {getStatusBadge(task?.status, task?.taskType)}
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                      <div className="flex items-start space-x-2">
                        <Calendar className="text-gray-400 flex-shrink-0 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-600">Date</p>
                          <p className="text-sm font-medium text-gray-900">
                            {new Date(task?.date)?.toLocaleDateString('fr-FR')}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-start space-x-2">
                        <Clock className="text-gray-400 flex-shrink-0 mt-0.5" size={16} />
                        <div>
                          <p className="text-xs text-gray-600">Créneau</p>
                          <p className="text-sm font-medium text-gray-900">{task?.time_window || 'Non défini'}</p>
                        </div>
                      </div>
                    </div>

                    {isPickup && (task?.client_name || task?.client_phone || task?.client_city || collectObject) && (
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-100 mb-3 space-y-1.5">
                        {task?.client_name && (
                          <div className="flex items-center gap-2">
                            <User className="text-gray-400 flex-shrink-0" size={14} />
                            <span className="text-sm font-medium text-gray-800">{task?.client_name}</span>
                          </div>
                        )}
                        {task?.client_phone && (
                          <div className="flex items-center gap-2">
                            <Phone className="text-gray-400 flex-shrink-0" size={14} />
                            <span className="text-sm text-gray-700">{task?.client_phone}</span>
                          </div>
                        )}
                        {task?.client_city && (
                          <div className="flex items-center gap-2">
                            <MapPin className="text-gray-400 flex-shrink-0" size={14} />
                            <span className="text-sm text-gray-700">{task?.client_city}</span>
                          </div>
                        )}
                        {collectObject && (
                          <div className="flex items-start gap-2">
                            <FileText className="text-gray-400 flex-shrink-0 mt-0.5" size={14} />
                            <span className="text-sm text-gray-700">{collectObject}</span>
                          </div>
                        )}
                      </div>
                    )}

                    {task?.taskType === 'ramassage' && !isDone && (
                      <button
                        onClick={(e) => { e?.stopPropagation(); handleTaskClick(task); }}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 active:bg-green-800 transition-colors min-h-[44px] text-sm sm:text-base"
                      >
                        <Eye size={18} />
                        <span>Voir les détails</span>
                      </button>
                    )}

                    {task?.taskType === 'ramassage' && isDone && (
                      <button
                        onClick={(e) => { e?.stopPropagation(); handleTaskClick(task); }}
                        className="w-full flex items-center justify-center space-x-2 px-4 py-2.5 bg-gray-200 text-gray-600 rounded-lg transition-colors min-h-[44px] text-sm sm:text-base"
                      >
                        <Eye size={18} />
                        <span>Voir les détails</span>
                      </button>
                    )}

                    {task?.taskType === 'depot' && (
                      <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                        <div className="flex items-start space-x-2">
                          <User className="text-gray-400 flex-shrink-0 mt-0.5" size={16} />
                          <div className="flex-1">
                            <p className="text-xs text-gray-600">Client</p>
                            <p className="text-sm font-medium text-gray-900">{task?.client_name}</p>
                            <p className="text-xs text-gray-600 mt-1">{task?.client_phone}</p>
                            <p className="text-xs text-gray-600 mt-1">{task?.client_address}, {task?.client_city}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              };

              return (
                <>
                  {activeTasks?.map(task => renderTaskCard(task, false))}
                  {doneTasks?.length > 0 && (
                    <>
                      <div className="pt-2 pb-1">
                        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Terminées aujourd'hui</h3>
                      </div>
                      {doneTasks?.map(task => renderTaskCard(task, true))}
                    </>
                  )}
                </>
              );
            })()
          )}
        </div>
      </div>

      {/* Barrel Deposit Detail Modal */}
      {showDepositModal && selectedDeposit && (
        <BarrelDepositDetailModal
          deposit={selectedDeposit}
          onClose={handleDepositModalClose}
          onSuccess={handleDepositSuccess}
        />
      )}
    </div>
  );
};

export default DriverDashboard;